import React, { CSSProperties, useCallback } from 'react';
import { alpha, darken, Icon, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            position      : 'relative',
            overflow      : 'hidden',
            '&:hover'     : {
                '& $controlOverlay': {
                    display: 'block',
                },
            },
        },

        videoImage: {
            width     : '100%',
            objectFit : 'contain',
            display   : 'inline-block',
            zIndex    : 1,
            minHeight : 150,
            background: darken(theme.palette.background.paper, 0.1),
        },

        controlOverlay: {
            display   : 'none',
            width     : '100%',
            height    : '100%',
            top       : 0,
            left      : 0,
            position  : 'absolute',
            background: alpha(theme.palette.text.primary, 0.6),
            color     : theme.palette.getContrastText(theme.palette.text.primary),
            cursor    : 'pointer',
            zIndex    : 2,
        },

        playPauseIcon: {
            position : 'absolute',
            left     : '50%',
            top      : '50%',
            transform: 'translate3D(-50%, -50%, 0)',
            fontSize : 75,
        },
    };
}, { name: 'VideoStreamPlayer', index: 1 });


export interface VideoStreamPlayerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly isPlaying: boolean;
    readonly currentVideoImage?: string | undefined;
    readonly onPlay?: ()=> void | undefined;
    readonly onPause?: ()=> void | undefined;
}


/**
 * This is a Generic Video Stream player component that can be used for playing videos from a given static images.
 * The parent container will need to provide the images to be displayed and handle the play/pause events.
 */
export const VideoStreamPlayer: React.FC<VideoStreamPlayerProps> = (props: VideoStreamPlayerProps) => {
    const classes = useStyle();
    const { onPlay: onPlayHandler, onPause:onPauseHandler } = props;


    const toggleIsPlayingState = useCallback(() => {
        if (props.isPlaying && onPauseHandler) {
            onPauseHandler();
        } else if (!props.isPlaying && onPlayHandler) {
            onPlayHandler();
        }
    }, [onPauseHandler, onPlayHandler, props.isPlaying]);


    return (
        <div
            className={clsx(props.className, classes.root)}
            style={props.style}
        >
            {/* Create an overlay on top of the image that the user can click on to play/pause the video */}
            <div
                className={classes.controlOverlay}
                onClick={toggleIsPlayingState}
            >
                {props.isPlaying ? (
                    <Icon className={classes.playPauseIcon}>pause</Icon>
                ) : (
                    <Icon className={classes.playPauseIcon}>play_arrow</Icon>
                )}
            </div>
            <img
                className={classes.videoImage}
                src={props.currentVideoImage ?? ''}
                alt={props.currentVideoImage ?? ''}
            />
        </div>
    );
};
